import React, { ChangeEvent, useEffect, useState } from "react";
import "./QuestionsManager.style.css";
import {
  Accordion,
  Button,
  Card,
  Col,
  Container,
  Form,
  Row,
  InputGroup,
  Alert,
  Modal,
  ListGroup,
  Stack,
} from "react-bootstrap";
import { Section } from "../../../models/Section";
import { AuditQuestion } from "../../../models/AuditQuestion";
import * as Icon from "react-bootstrap-icons";
import { Subsection } from "../../../models/Subsection";
import AnswerService from "../../../services/auditAnswer.service";
import { AuditAnswer } from "../../../models/AuditAnswer";
import { TagsInput } from "react-tag-input-component";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faLink,
  faBan,
  faLinkSlash,
  faAdd,
} from "@fortawesome/free-solid-svg-icons";
import AuditQuestionService from "../../../services/auditQuestion.service";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import DeleteButton from "../../../Helpers/deleteButton";
import ImageUpload from "./ImageUpload";
import FileUpload from "./FileUpload";
import { useLoading } from "../../App/LoadingContext";
import { useConfirmationModalContext } from "../../../Helpers/modalConfirmationContext";

type Props = {
  subsection: Subsection;
};

export const AuditQuestionsManager = (props: Props) => {
  const [questions, setQuestions] = useState<AuditQuestion[]>([]);
  const [selectedColumns, setSelectedColumns] = useState<string[]>([]);
  const [showPopup, setShowPopup] = useState<boolean>(false);
  const [popupTitle, setPopupTitle] = useState<string>("");
  const [popupMessage, setPopupMessage] = useState<string>("");
  const [isErrorMessage, setIsErrorMessage] = useState<boolean>(false);
  const { wrapWithLoading } = useLoading();
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [linkingAnswer, setLinkingAnswer] = useState<AuditAnswer | null>(null);
  const modalContext = useConfirmationModalContext();

  async function loadQuestions() {
    let data = await AuditQuestionService.getQuestions(props.subsection.id);
    const sortedData = data.sort((a, b) => a.position - b.position);
    setQuestions(sortedData);
  }

  async function onQuestionInsert() {
    const newPosition =
      questions.length > 0
        ? Math.max(...questions.map((q) => q.position)) + 1
        : 1;

    let question: AuditQuestion = {
      id: 0,
      questionDesc: "Nueva pregunta",
      questionTypeId: 1,
      subsectionId: props.subsection.id,
      position: newPosition,
      required: false,
    };

    var result = AuditQuestionService.insertQuestion({
      id: question.id,
      questionDesc: question.questionDesc,
      questionTypeId: question.questionTypeId,
      subsectionId: question.subsectionId,
      position: newPosition,
    });

    result
      .then((response) => {
        var resp = response as Response;

        if (resp.ok) {
          return resp.json();
        }
      })
      .then((result: AuditQuestion) => {
        question.id = result.id;

        question.auditAnswers = [];

        if (
          [1, 4, 5, 6, 7, 9, 10, 11, 12, 13].includes(question.questionTypeId)
        ) {
          if (result.auditAnswers != undefined)
            question.auditAnswers?.push({
              answerDesc: "",
              auditQuestionId: question.id,
              id: result.id,
            });
        }

        setQuestions([...questions, question]);
      });
  }

  const onQuestionDelete = wrapWithLoading(async (question: AuditQuestion) => {
    var result = AuditQuestionService.deleteQuestion(question);

    result.catch((error) => {
      setPopupMessage("Error al eliminar el registro");
      setPopupTitle("Error");
      setIsErrorMessage(true);
      setShowPopup(true);

      window.scrollTo(0, 0);

      return;
    });

    result.then((response) => {
      var resp = response as Response;

      if (resp.ok) {
        const newQuestions = questions.filter(
          (current) => current.id !== question.id
        );
        setQuestions(newQuestions);

        setPopupMessage("Se eliminó la pregunta exitosamente");
        setPopupTitle("Registro eliminado");
        setIsErrorMessage(false);
        setShowPopup(true);

        window.scrollTo(0, 0);

        return;
      } else {
        setPopupMessage(
          "No se puede eliminar el registro porque hay información relacionada"
        );
        setPopupTitle("Error");
        setIsErrorMessage(true);
        setShowPopup(true);

        window.scrollTo(0, 0);

        return;
      }
    });

    // After successful delete
    const updatedQuestions = questions
      .filter((q) => q.id !== question.id)
      .map((q, index) => ({ ...q, position: index + 1 }));

    setQuestions(updatedQuestions);

    // Update positions in DB
    await AuditQuestionService.updateQuestionPositions(
      updatedQuestions.map((q) => ({
        questionId: q.id,
        newPosition: q.position,
      }))
    );
  });

  const onQuestionTypeChanged = async (
    questionTypeId: any,
    questionId: number,
    event: ChangeEvent
  ) => {
    const confirmed = await modalContext.showConfirmation(
      "Confirmación",
      <div>
        <p>¿Estás seguro que deseas cambiar el tipo de pregunta?</p>
        <p>Se perderan los cambios realizados</p>
      </div>
    );

    if (!confirmed) return;

    wrapWithLoading(async () => {
      const myNewList = [...questions];
      const currentQuestion = myNewList.find((a) => a.id == questionId);

      if (currentQuestion) {
        currentQuestion.questionTypeId = Number(questionTypeId);

        var result = AuditQuestionService.updateQuestion({
          id: currentQuestion.id,
          questionDesc: currentQuestion.questionDesc,
          questionTypeId: currentQuestion.questionTypeId,
          subsectionId: currentQuestion.subsectionId,
          position: currentQuestion.position,
          required: false,
        });

        result.catch((error) => {
          setPopupMessage("Error al eliminar el registro");
          setPopupTitle("Error");
          setIsErrorMessage(true);
          setShowPopup(true);

          window.scrollTo(0, 0);

          return;
        });

        result
          .then((response) => {
            var resp = response as unknown as Response;

            if (resp.ok) {
              return resp.json();
            } else {
              return;
            }
          })
          .then((newQuestion: AuditQuestion) => {
            currentQuestion.auditAnswers?.splice(0);

            if (
              [1, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13].includes(
                currentQuestion.questionTypeId
              )
            ) {
              if (newQuestion.auditAnswers != undefined)
                currentQuestion.auditAnswers?.push({
                  auditQuestionId: currentQuestion.id,
                  answerDesc: "",
                  comment: "",
                  id: newQuestion.auditAnswers[0].id,
                });
            }

            currentQuestion.required = false;
            setQuestions(myNewList);
          });
      }
    })();
  };

  function onQuestionUpdate(questionDesc: string, questionId: number) {
    const myNewList = [...questions];
    const currentQuestion = myNewList.find((a) => a.id == questionId);

    if (currentQuestion) {
      currentQuestion.questionDesc = questionDesc;

      setQuestions(myNewList);
    }
  }

  function onAnswerUpdate(
    answerDesc: string,
    answerId: any,
    questionId: number
  ) {
    const myNewList = [...questions];
    const currentQuestion = myNewList.find((a) => a.id == questionId);

    if (currentQuestion) {
      const currentAnswer = currentQuestion.auditAnswers?.find(
        (a) => a.id == answerId
      );

      if (currentAnswer) {
        currentAnswer.answerDesc = answerDesc;

        setQuestions(myNewList);
      }
    }
  }

  function onTagRemoved(answerDesc: string, answerId: any, questionId: number) {
    const myNewList = [...questions];
    const currentQuestion = myNewList.find((a) => a.id == questionId);

    if (currentQuestion) {
      const currentAnswer = currentQuestion.auditAnswers?.find(
        (a) => a.id == answerId
      );

      if (currentAnswer) {
        let tagArray = JSON.parse(
          currentAnswer.answerDesc ? currentAnswer.answerDesc : ""
        );

        currentAnswer.answerDesc = JSON.stringify(
          tagArray.filter((item: any) => item !== answerDesc)
        );

        setQuestions(myNewList);

        onAnswerChange(currentAnswer);
      }
    }
  }

  async function onQuestionChange(currentQuestion: AuditQuestion) {
    var result = AuditQuestionService.updateQuestion(currentQuestion);
  }

  async function onRequiredChange(question: AuditQuestion, value: boolean) {
    const myNewList = [...questions];
    const currentQuestion = myNewList.find((a) => a.id == question.id);

    if (currentQuestion) {
      currentQuestion.required = value;

      setQuestions(myNewList);

      AuditQuestionService.updateQuestion(currentQuestion);
    }
  }

  async function onAnswerChange(currentAnswer: AuditAnswer) {
    AnswerService.updateAnswer(currentAnswer);
  }

  function onMatrixUpdate(
    answerDesc: string,
    answerId: any,
    questionId: number,
    arrayIndex: number
  ) {
    const myNewList = [...questions];
    const currentQuestion = myNewList.find((a) => a.id == questionId);

    if (currentQuestion) {
      const currentAnswer = currentQuestion.auditAnswers?.find(
        (a) => a.id == answerId
      );

      if (currentAnswer) {
        const previousValue = currentAnswer.answerDesc;

        if (arrayIndex == 0)
          currentAnswer.answerDesc =
            answerDesc +
            "}" +
            (previousValue?.split("}")[1] != undefined
              ? previousValue?.split("}")[1]
              : "[]");
        else
          currentAnswer.answerDesc =
            (previousValue?.split("}")[0] != undefined
              ? previousValue?.split("}")[0]
              : "[]") +
            "}" +
            answerDesc;

        setQuestions(myNewList);
      }
    }
  }

  function onMatrixTagRemoved(
    answerDesc: string,
    answerId: any,
    questionId: number,
    arrayIndex: number
  ) {
    const myNewList = [...questions];
    const currentQuestion = myNewList.find((a) => a.id == questionId);

    if (currentQuestion) {
      const currentAnswer = currentQuestion.auditAnswers?.find(
        (a) => a.id == answerId
      );

      if (currentAnswer) {
        const previousValue = currentAnswer.answerDesc;

        if (arrayIndex == 0) {
          let tagArray = JSON.parse(
            previousValue ? previousValue?.split("}")[0] : ""
          );

          currentAnswer.answerDesc =
            JSON.stringify(
              tagArray.filter((item: any) => item !== answerDesc)
            ) +
            "}" +
            previousValue?.split("}")[1];
        } else {
          let tagArray = JSON.parse(
            previousValue ? previousValue?.split("}")[1] : ""
          );

          currentAnswer.answerDesc =
            previousValue?.split("}")[0] +
            "}" +
            JSON.stringify(tagArray.filter((item: any) => item !== answerDesc));
        }

        setQuestions(myNewList);

        onAnswerChange(currentAnswer);
      }
    }
  }

  function onCommentUpdate(comment: string, answerId: any, questionId: number) {
    const myNewList = [...questions];
    const currentQuestion = myNewList.find((a) => a.id == questionId);

    if (currentQuestion) {
      const currentAnswer = currentQuestion.auditAnswers?.find(
        (a) => a.id == answerId
      );

      if (currentAnswer) {
        currentAnswer.comment = comment;

        setQuestions(myNewList);
      }
    }
  }

  async function onAnswerInsert(question: AuditQuestion) {
    let answer: AuditAnswer = {
      id: 0,
      answerDesc: "nueva opción",
      auditQuestionId: question.id,
      comment: "",
    };

    var result = AnswerService.insertAnswer(answer);

    result
      .then((response) => {
        var resp = response as Response;

        if (resp.ok) {
          return resp.json();
        }
      })
      .then((result) => {
        const myNewList = [...questions];
        const currentQuestion = myNewList.find((a) => a.id == question.id);

        answer.id = result;

        if (currentQuestion) {
          if (currentQuestion.auditAnswers) {
            currentQuestion.auditAnswers.push(answer);
          } else {
            currentQuestion.auditAnswers = [answer];
          }

          setQuestions(myNewList);
        }
      });
  }

  const handleStartLinking = (answer: AuditAnswer) => {
    setLinkingAnswer(answer);
    setShowLinkModal(true);
  };

  const handleLinkQuestionToAnswer = wrapWithLoading(
    async (targetQuestionId: number) => {
      if (!linkingAnswer) return;
      await AuditQuestionService.linkQuestion(
        linkingAnswer.id!,
        targetQuestionId
      );
      await loadQuestions(); // Recarga las preguntas para ver el enlace
      setShowLinkModal(false);
      setLinkingAnswer(null);
    }
  );

  const handleUnlinkQuestion = async (answer: AuditAnswer) => {
    const confirmed = await modalContext.showConfirmation(
      "Confirmación",
      <div>
        <p>¿Estás seguro que deseas remover el enlace? </p>
      </div>
    );

    if (!confirmed) return;

    wrapWithLoading(async () => {
      await AuditQuestionService.unlinkQuestion(answer.id!);
      await loadQuestions(); // Recarga las preguntas para ver el cambio
    })();
  };

  async function onAnswerDelete(answer: AuditAnswer, questionId: number) {
    var result = AnswerService.deleteAnswer(answer);

    result.catch((error) => {
      setPopupMessage("Error al eliminar el registro");
      setPopupTitle("Error");
      setIsErrorMessage(true);
      setShowPopup(true);

      window.scrollTo(0, 0);

      return;
    });

    result.then((response) => {
      var resp = response as Response;

      if (resp.ok) {
        const myNewList = [...questions];
        const currentQuestion = myNewList.find((a) => a.id == questionId);

        if (currentQuestion) {
          const newAnswers = currentQuestion.auditAnswers?.filter(
            (a) => a.id != answer.id
          );

          currentQuestion.auditAnswers = newAnswers;

          setQuestions(myNewList);
        }

        setPopupMessage("Se eliminó la subsección exitosamente");
        setPopupTitle("Registro eliminado");
        setIsErrorMessage(false);
        setShowPopup(true);

        window.scrollTo(0, 0);

        return;
      } else {
        setPopupMessage(
          "No se puede eliminar el registro porque hay información relacionada"
        );
        setPopupTitle("Error");
        setIsErrorMessage(true);
        setShowPopup(true);

        window.scrollTo(0, 0);

        return;
      }
    });
  }

  const handleDragEnd = async (result: any) => {
    if (!result.destination) return;

    // Create a new array from current questions
    const items = Array.from(questions);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    // Update positions in the local state
    const updatedQuestions = items.map((question, index) => ({
      ...question,
      position: index + 1,
    }));

    setQuestions(updatedQuestions);

    // Update positions in the database
    try {
      await AuditQuestionService.updateQuestionPositions(
        updatedQuestions.map((q) => ({
          questionId: q.id,
          newPosition: q.position,
        }))
      );
    } catch (error) {
      setPopupMessage("Error al actualizar las posiciones");
      setPopupTitle("Error");
      setIsErrorMessage(true);
      setShowPopup(true);
    }
  };

  useEffect(() => {
    loadQuestions();
  }, []);

  // Don't remove this section because avoid a commun issue on drag and drop functionality
  // Details on: https://github.com/atlassian/react-beautiful-dnd/issues/2396
  const [droppableId, setDroppableId] = useState("hello");
  useEffect(() => {
    if (questions.length) {
      setDroppableId("questions");
    }
  }, [questions.length]);
  //////////////////////////////////////////////////////////////////////////////////////
  useEffect(() => {
    if (showPopup) {
      const timer = setTimeout(() => {
        setShowPopup(false);
      }, 6000);

      return () => clearTimeout(timer);
    }
  }, [showPopup]);

  return (
    <div>
      <div className="notification-container">
        {showPopup && (
          <Alert
            variant={isErrorMessage ? "danger" : "success"}
            onClose={() => setShowPopup(false)}
            className="notification-alert"
            dismissible
          >
            <Alert.Heading>{popupTitle}</Alert.Heading>
            <p>{popupMessage}</p>
          </Alert>
        )}
      </div>

      <Modal
        show={showLinkModal}
        onHide={() => setShowLinkModal(false)}
        centered
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>Enlazar pregunta</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            Selecciona la pregunta que se mostrará cuando el usuario elija la
            opción: <strong>"{linkingAnswer?.answerDesc}"</strong>
          </p>
          <ListGroup style={{ maxHeight: "400px", overflowY: "auto" }}>
            {questions
              .filter((q) => q.id !== linkingAnswer?.auditQuestionId)
              .map((question) => (
                <ListGroup.Item
                  key={question.id}
                  action
                  className="d-flex justify-content-between align-items-center"
                >
                  <span>
                    <span className="badge bg-primary me-2">
                      {question.position}
                    </span>{" "}
                    {question.questionDesc}
                  </span>
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={() => handleLinkQuestionToAnswer(question.id)}
                  >
                    <a>
                      <FontAwesomeIcon
                        icon={faLink}
                        style={{
                          paddingRight: "3px",
                        }}
                      />
                      Enlazar
                    </a>
                  </Button>
                </ListGroup.Item>
              ))}
          </ListGroup>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowLinkModal(false)}>
            <a>
              <FontAwesomeIcon
                icon={faBan}
                style={{
                  paddingRight: "3px",
                }}
              />
              Cancelar
            </a>
          </Button>
        </Modal.Footer>
      </Modal>

      <Container>
        <Card>
          <Card.Body>
            <Card.Title>Subsección: {props.subsection.name}</Card.Title>
            <hr />
            <Container>
              <Row>
                <Col>
                  <DragDropContext onDragEnd={handleDragEnd}>
                    <Droppable droppableId={droppableId}>
                      {(provided) => (
                        <div
                          {...provided.droppableProps}
                          ref={provided.innerRef}
                        >
                          <Accordion
                            defaultActiveKey={questions.at(0)?.id.toString()}
                          >
                            {questions.map((question, index) => (
                              <Draggable
                                key={question.id.toString()}
                                draggableId={question.id.toString()}
                                index={index}
                              >
                                {(provided, snapshot) => (
                                  <div
                                    ref={provided.innerRef}
                                    {...provided.draggableProps}
                                    style={{
                                      ...provided.draggableProps.style,
                                      transition: "all 0.3s ease",
                                      backgroundColor: snapshot.isDragging
                                        ? "#f8f9fa"
                                        : "white",
                                    }}
                                  >
                                    <Accordion.Item
                                      eventKey={question.id.toString()}
                                    >
                                      <Accordion.Header>
                                        <Row className="p-0 w-100 align-items-center">
                                          {/* Drag Handle */}
                                          <Col
                                            className="drag-column"
                                            xs={1}
                                            {...provided.dragHandleProps}
                                            onClick={(e) => e.stopPropagation()}
                                          >
                                            <Icon.GripVertical className="text-muted" />
                                          </Col>
                                          <Col
                                            className="badge-column"
                                            xs={1}
                                            onClick={(e) => e.stopPropagation()}
                                          >
                                            {/* Badge */}
                                            <span className="badge bg-primary badge-position">
                                              {question.position}
                                            </span>
                                          </Col>
                                          <Col
                                            xs={6}
                                            onClick={(e) => e.stopPropagation()}
                                          >
                                            {question.questionTypeId === 6 ? (
                                              <Form.Control
                                                as="textarea"
                                                rows={4}
                                                style={{
                                                  resize: "vertical",
                                                }}
                                                value={question.questionDesc}
                                                onChange={(e) => {
                                                  onQuestionUpdate(
                                                    e.target.value,
                                                    question.id
                                                  );
                                                }}
                                                onBlur={(e) => {
                                                  onQuestionChange(question);
                                                }}
                                              />
                                            ) : (
                                              <Form.Control
                                                size="sm"
                                                type="text"
                                                value={question.questionDesc}
                                                onChange={(e) => {
                                                  onQuestionUpdate(
                                                    e.target.value,
                                                    question.id
                                                  );
                                                }}
                                                onBlur={(e) => {
                                                  onQuestionChange(question);
                                                }}
                                              />
                                            )}
                                          </Col>
                                          <Col
                                            xs={6}
                                            md={4}
                                            onClick={(e) => e.stopPropagation()}
                                          >
                                            <InputGroup
                                              className="mb-0"
                                              size="sm"
                                            >
                                              <Stack
                                                direction="horizontal"
                                                gap={2}
                                              >
                                                <Form.Select
                                                  aria-label="Default select example"
                                                  size="sm"
                                                  value={
                                                    question.questionTypeId
                                                  }
                                                  defaultValue={2}
                                                  onChange={(e) => {
                                                    onQuestionTypeChanged(
                                                      e.target.value,
                                                      question.id,
                                                      e
                                                    );
                                                  }}
                                                >
                                                  <option value="1">
                                                    Abierta
                                                  </option>
                                                  <option value="2">
                                                    Selección Sencilla
                                                  </option>
                                                  <option value="3">
                                                    Selección Múltiple
                                                  </option>
                                                  <option value="4">
                                                    Columnas Múltiples
                                                  </option>
                                                  <option value="5">
                                                    Abierta Multilínea
                                                  </option>
                                                  <option value="6">
                                                    Informativo
                                                  </option>
                                                  <option value="7">
                                                    Fecha
                                                  </option>
                                                  <option value="8">
                                                    Matríz
                                                  </option>
                                                  <option value="9">
                                                    Imagen
                                                  </option>
                                                  <option value="10">
                                                    Archivo
                                                  </option>
                                                  <option value="11">
                                                    Archivo plantilla
                                                  </option>
                                                  <option value="12">
                                                    Moneda
                                                  </option>
                                                  <option value="13">
                                                    Enlace
                                                  </option>
                                                </Form.Select>
                                                <Form.Check
                                                  type="switch"
                                                  disabled={[6, 9, 13].includes(
                                                    question.questionTypeId
                                                  )}
                                                  id={
                                                    "questionRequired" +
                                                    question.id
                                                  }
                                                  label="Requerido"
                                                  checked={question.required}
                                                  onChange={(e) => {
                                                    onRequiredChange(
                                                      question,
                                                      e.target.checked
                                                    );
                                                  }}
                                                />
                                                <DeleteButton
                                                  onClick={() =>
                                                    onQuestionDelete(question)
                                                  }
                                                />
                                              </Stack>
                                            </InputGroup>
                                          </Col>
                                        </Row>
                                      </Accordion.Header>
                                      <Accordion.Body>
                                        {question.questionTypeId === 9 ? (
                                          <>
                                            <ImageUpload
                                              key={`image-upload-${question.id}`}
                                              auditQuestionId={question.id}
                                              onUploadSuccess={() =>
                                                console.log("Upload successful")
                                              }
                                            />
                                          </>
                                        ) : question.questionTypeId === 11 ? (
                                          <>
                                            <FileUpload
                                              key={`file-upload-${question.id}`}
                                              auditQuestionId={question.id}
                                              onUploadSuccess={() =>
                                                console.log(
                                                  "File upload successful"
                                                )
                                              }
                                            />
                                          </>
                                        ) : (
                                          <>
                                            {question.auditAnswers?.map(
                                              (answer: AuditAnswer) => (
                                                <>
                                                  <Row>
                                                    <Col md={8}>
                                                      <InputGroup size="sm">
                                                        {question.questionTypeId ==
                                                          2 && (
                                                          <InputGroup.Radio
                                                            disabled
                                                            className="check-preview bg-primary"
                                                            id={
                                                              "check" +
                                                              answer.id
                                                            }
                                                          />
                                                        )}
                                                        {question.questionTypeId ==
                                                          3 && (
                                                          <InputGroup.Checkbox
                                                            id={
                                                              "check" +
                                                              answer.id
                                                            }
                                                            disabled
                                                            className="check-preview"
                                                          />
                                                        )}
                                                        {[2, 3].includes(
                                                          question.questionTypeId
                                                        ) && (
                                                          <Form.Control
                                                            type="text"
                                                            placeholder={
                                                              question.questionTypeId ==
                                                              1
                                                                ? ""
                                                                : "Agrega una opción"
                                                            }
                                                            value={
                                                              answer.answerDesc
                                                            }
                                                            readOnly={
                                                              question.questionTypeId ==
                                                              1
                                                            }
                                                            onChange={(e) => {
                                                              onAnswerUpdate(
                                                                e.target.value,
                                                                answer.id,
                                                                question.id
                                                              );
                                                            }}
                                                            onBlur={(e) => {
                                                              onAnswerChange(
                                                                answer
                                                              );
                                                            }}
                                                          />
                                                        )}
                                                        <DeleteButton
                                                          onClick={() =>
                                                            onAnswerDelete(
                                                              answer,
                                                              question.id
                                                            )
                                                          }
                                                          hidden={[
                                                            1, 4, 5, 6, 7, 8,
                                                            10, 12, 13,
                                                          ].includes(
                                                            question.questionTypeId
                                                          )}
                                                        />
                                                      </InputGroup>
                                                      {question.questionTypeId ==
                                                        4 && (
                                                        <TagsInput
                                                          value={JSON.parse(
                                                            answer.answerDesc !=
                                                              undefined
                                                              ? answer.answerDesc
                                                                ? answer.answerDesc
                                                                : "[]"
                                                              : "[]"
                                                          )}
                                                          onChange={(value) =>
                                                            onAnswerUpdate(
                                                              JSON.stringify(
                                                                value
                                                              ),
                                                              answer.id,
                                                              question.id
                                                            )
                                                          }
                                                          name={
                                                            "multipleColumns" +
                                                            answer.id
                                                          }
                                                          placeHolder="Ingrese las columnas seguido de Enter"
                                                          onBlur={() =>
                                                            onAnswerChange(
                                                              answer
                                                            )
                                                          }
                                                          onRemoved={(value) =>
                                                            onTagRemoved(
                                                              value,
                                                              answer.id,
                                                              question.id
                                                            )
                                                          }
                                                        />
                                                      )}
                                                      {question.questionTypeId ==
                                                        8 && (
                                                        <>
                                                          <TagsInput
                                                            value={JSON.parse(
                                                              answer.answerDesc !=
                                                                undefined &&
                                                                answer.answerDesc.split(
                                                                  "}"
                                                                ).length > 1
                                                                ? answer.answerDesc
                                                                  ? answer.answerDesc.split(
                                                                      "}"
                                                                    )[0]
                                                                  : "[]"
                                                                : "[]"
                                                            )}
                                                            onChange={(value) =>
                                                              onMatrixUpdate(
                                                                JSON.stringify(
                                                                  value
                                                                ),
                                                                answer.id,
                                                                question.id,
                                                                0
                                                              )
                                                            }
                                                            name={
                                                              "matrixRow" +
                                                              answer.id
                                                            }
                                                            placeHolder="Ingrese las filas seguido de Enter"
                                                            onBlur={() =>
                                                              onAnswerChange(
                                                                answer
                                                              )
                                                            }
                                                            onRemoved={(
                                                              value
                                                            ) =>
                                                              onMatrixTagRemoved(
                                                                value,
                                                                answer.id,
                                                                question.id,
                                                                0
                                                              )
                                                            }
                                                          />
                                                          <TagsInput
                                                            value={JSON.parse(
                                                              answer.answerDesc !=
                                                                undefined &&
                                                                answer.answerDesc.split(
                                                                  "}"
                                                                ).length > 1
                                                                ? answer.answerDesc
                                                                  ? answer.answerDesc.split(
                                                                      "}"
                                                                    )[1]
                                                                  : "[]"
                                                                : "[]"
                                                            )}
                                                            onChange={(value) =>
                                                              onMatrixUpdate(
                                                                JSON.stringify(
                                                                  value
                                                                ),
                                                                answer.id,
                                                                question.id,
                                                                1
                                                              )
                                                            }
                                                            name={
                                                              "matrixColumn" +
                                                              answer.id
                                                            }
                                                            placeHolder="Ingrese las columnas seguido de Enter"
                                                            onBlur={() =>
                                                              onAnswerChange(
                                                                answer
                                                              )
                                                            }
                                                            onRemoved={(
                                                              value
                                                            ) =>
                                                              onMatrixTagRemoved(
                                                                value,
                                                                answer.id,
                                                                question.id,
                                                                1
                                                              )
                                                            }
                                                          />
                                                        </>
                                                      )}
                                                    </Col>
                                                  </Row>
                                                  {![4, 6, 8].includes(
                                                    question.questionTypeId
                                                  ) && (
                                                    <Row>
                                                      <Col>
                                                        <Form.Control
                                                          size="sm"
                                                          type="text"
                                                          placeholder={
                                                            question.questionTypeId ===
                                                            13
                                                              ? "Ingrese el enlace aquí"
                                                              : "Agrega una observación"
                                                          }
                                                          value={answer.comment}
                                                          onChange={(e) => {
                                                            onCommentUpdate(
                                                              e.target.value,
                                                              answer.id,
                                                              question.id
                                                            );
                                                          }}
                                                          onBlur={(e) => {
                                                            onAnswerChange(
                                                              answer
                                                            );
                                                          }}
                                                        />
                                                      </Col>
                                                    </Row>
                                                  )}
                                                  <Row>
                                                    <Col>
                                                      {answer.linkedQuestionId ? (
                                                        <Button
                                                          size="sm"
                                                          variant="danger"
                                                          onClick={() =>
                                                            handleUnlinkQuestion(
                                                              answer
                                                            )
                                                          }
                                                        >
                                                          <a>
                                                            <FontAwesomeIcon
                                                              icon={faLinkSlash}
                                                              style={{
                                                                paddingRight:
                                                                  "3px",
                                                              }}
                                                            />
                                                            Remover enlace
                                                          </a>
                                                        </Button>
                                                      ) : (
                                                        <Button
                                                          hidden={
                                                            question.questionTypeId !==
                                                            2
                                                          }
                                                          size="sm"
                                                          variant="dark"
                                                          onClick={() =>
                                                            handleStartLinking(
                                                              answer
                                                            )
                                                          }
                                                        >
                                                          <a>
                                                            <FontAwesomeIcon
                                                              icon={faLink}
                                                              style={{
                                                                paddingRight:
                                                                  "3px",
                                                              }}
                                                            />
                                                            Enlazar pregunta
                                                          </a>
                                                        </Button>
                                                      )}
                                                    </Col>
                                                  </Row>
                                                </>
                                              )
                                            )}
                                          </>
                                        )}
                                        {[2, 3].includes(
                                          question.questionTypeId
                                        ) && (
                                          <>
                                            <hr />
                                            <Row>
                                              <Col>
                                                <Button
                                                  size="sm"
                                                  variant="primary"
                                                  onClick={() =>
                                                    onAnswerInsert(question)
                                                  }
                                                >
                                                  <a>
                                                    <FontAwesomeIcon
                                                      icon={faAdd}
                                                      style={{
                                                        paddingRight: "3px",
                                                      }}
                                                    />
                                                    Nueva respuesta
                                                  </a>
                                                </Button>
                                              </Col>
                                            </Row>
                                          </>
                                        )}
                                      </Accordion.Body>
                                    </Accordion.Item>
                                  </div>
                                )}
                              </Draggable>
                            ))}
                          </Accordion>
                          {provided.placeholder}
                        </div>
                      )}
                    </Droppable>
                  </DragDropContext>
                </Col>
              </Row>
              <Row className="pt-3">
                <Col>
                  <Button variant="primary" onClick={onQuestionInsert}>
                    <a>
                      <FontAwesomeIcon
                        icon={faAdd}
                        style={{
                          paddingRight: "3px",
                        }}
                      />
                      Nueva pregunta
                    </a>
                  </Button>
                </Col>
              </Row>
            </Container>
          </Card.Body>
        </Card>
      </Container>
    </div>
  );
};
